import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useDonorPlan } from '@/composables/useDonorPlan'

/** Shared across composable instances so the greeting does not flip mid-session. */
let sessionFirstVisitFrozen: boolean | null = null

type DonorProfile = {
  emailAddress?: string
  dashboardSeen?: boolean
}

function readDonorProfile(
  store: ReturnType<typeof useStore>
): DonorProfile | undefined {
  return (store.state as { donor?: { userDetails?: DonorProfile } }).donor
    ?.userDetails
}

/** GET /me has returned, email and dashboardSeen are both present. */
export function isDonorWelcomeProfileReady(
  user: DonorProfile | undefined
): boolean {
  return (
    Boolean(user?.emailAddress?.trim()) &&
    typeof user?.dashboardSeen === 'boolean'
  )
}

/**
 * First-visit vs returning dashboard greeting from GET /me `dashboardSeen`.
 * Free users: title only. Paid users: title + overview subtitle.
 */
export function useDashboardWelcome() {
  const store = useStore()
  const { isPaid, isPlanKnown } = useDonorPlan()

  const isWelcomeReady = computed(() =>
    isDonorWelcomeProfileReady(readDonorProfile(store))
  )

  const dashboardSeenOnLoad = readDonorProfile(store)?.dashboardSeen === true

  watch(
    () => readDonorProfile(store)?.emailAddress,
    () => {
      sessionFirstVisitFrozen = null
    }
  )

  const isFirstDashboardVisit = computed(() => {
    if (!isWelcomeReady.value) {
      return false
    }
    if (sessionFirstVisitFrozen === null) {
      sessionFirstVisitFrozen = !dashboardSeenOnLoad
    }
    return sessionFirstVisitFrozen
  })

  const showPaidDashboardSubtitle = computed(
    () => isWelcomeReady.value && isPlanKnown.value && isPaid.value
  )

  return {
    isWelcomeReady,
    isFirstDashboardVisit,
    showPaidDashboardSubtitle,
    dashboardSeenOnLoad,
  }
}
